import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import "../css/ProductDetails.css";
import products from "../data/products";
import ContactForm from "../components/ContactForm";
import YouTubeEmbed from "../components/EmbeddedYoutube"; // add this at the top


const FileTextIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ verticalAlign: "middle", marginRight: "6px" }} // ← add this
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
    <polyline points="10 9 9 9 8 9"></polyline>
  </svg>
);


const ProductDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [currentImage, setCurrentImage] = useState(location.state?.image || "");
  const [zoomImage, setZoomImage] = useState(null);


  const imageRef = React.useRef(null);
  const previewRef = React.useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [location]);

  const product = products.find((product) => product.id === parseInt(id));

  useEffect(() => {
    if (!product) return;
    const storedProduct = JSON.parse(localStorage.getItem("product"));
    if (storedProduct && storedProduct.id === product.id) {
      setCurrentImage("/" + storedProduct.image);
    } else if (product.img) {
      setCurrentImage("/" + product.img);
    }
  }, [product]);

  const handleImageClick = (image) => {
    setCurrentImage(image);
  };

  const handleMouseMove = (e) => {
    const preview = previewRef.current;
    const image = imageRef.current;
    if (!preview || !image) return;

    const bounds = image.getBoundingClientRect();
    const x = e.clientX - bounds.left;
    const y = e.clientY - bounds.top;

    const bgX = (x / image.offsetWidth) * 100;
    const bgY = (y / image.offsetHeight) * 100;

    preview.style.backgroundPosition = `${bgX}% ${bgY}%`;
  };

  const handleMouseEnter = () => {
    const preview = previewRef.current;
    const image = imageRef.current;
    if (!preview || !image) return;

    preview.style.display = "block";
    preview.style.backgroundImage = `url(${image.src})`;
  };

  const handleMouseLeave = () => {
    if (previewRef.current) {
      previewRef.current.style.display = "none";
    }
  };

  if (!product) {
    return <h2>Product not found</h2>;
  }

  // === RENDER PRODUCT OPTIONS IF THEY EXIST ===
  if (product.options && Array.isArray(product.options)) {
    return (
      <div className="product-options-container">
        <h1>{product.name}</h1>
        <p>{product.description}</p>
        <div className="product-options-grid">
          {product.options.map((option) => (
            <div
              key={option.id}
              className="product-option-card"
              onClick={() => navigate(`/products/${option.id}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  navigate(`/products/${option.id}`);
                }
              }}
            >
              <img
                src={option.img}
                alt={option.name}
                className="product-option-image"
              />
              <h3>{option.name}</h3>
              <p>{option.description}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const additionalImages = product.additionalImages || [];

  return (
    <div className="product-details-container">
      <div className="product-details-main">
        <div className="product-details-left">
          <div className="product-image-container">
            <div className="image-wrapper">
              <img
                src={currentImage || `/${product.img}`}
                className="product-image"
                ref={imageRef}
                onMouseMove={handleMouseMove}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                alt={product.name}
              />

              <img
                src="/TESTDEMO/made-in-usa.png"
                alt="Made in USA"
                className="usa-stamp"
              />
            </div>
            {additionalImages.length > 0 && (
              <div className="thumbnail-container">
                {additionalImages.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`Additional View ${index + 1}`}
                    className="thumbnail-image"
                    onClick={() => handleImageClick(image)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleImageClick(image);
                      }
                    }}
                  />
                ))}
              </div>
            )}
          </div>
          <div className="zoom-preview" ref={previewRef}></div>
        </div>

        <div className="product-details-right">
          <div className="product-title-container">
            <h1>{product.name}</h1>
            {product.brochure && (
              <a
                href={product.brochure}
                target="_blank"
                rel="noopener noreferrer"
                className="add-to-cart-button"
                download
              >
                <FileTextIcon />
                Get Product Brochure
              </a>
            )}
          </div>
          <p className="product-details-description">{product.description}</p>
        </div>
      </div>


      {/* === Conditionally render YouTube video === */}
      {product.youtube && (
        <div className="product-video-section" id="showcase">
          <h2>Watch Product Demo</h2>
          <YouTubeEmbed url={product.youtube} width="640" height="360" />
        </div>
      )}

      {product.specifications &&
        Object.keys(product.specifications).length > 0 && (
          <div className="product-specifications">
            <h2>Product Information</h2>
            <table className="specifications-table">
              <tbody>
                {Object.entries(product.specifications).map(([key, value]) => (
                  <tr key={key}>
                    <td className="spec-key">{key}</td>
                    <td className="spec-value">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }

      {product.code && (
      <div className="code-image-row">
        <img
          src="/TESTDEMO/HoseAssembly/cover-and-code-1.webp"
          alt="Product Illustration"
          className="code-img"
          onClick={() =>
            setZoomImage("/TESTDEMO/HoseAssembly/cover-and-code-1.webp")
          }
        />
        <img
          src="/TESTDEMO/HoseAssembly/cover-and-code-2.webp"
          alt="Product Illustration"
          className="code-img"
          onClick={() =>
            setZoomImage("/TESTDEMO/HoseAssembly/cover-and-code-2.webp")
          }
        />
      </div>
    )}

    {/* === Fullscreen Modal Zoom === */}
    {zoomImage && (
      <div className="zoom-overlay" onClick={() => setZoomImage(null)}>
        <div className="zoom-content" onClick={(e) => e.stopPropagation()}>
          <button className="zoom-close" onClick={() => setZoomImage(null)}>
            ✕
          </button>
          <img src={zoomImage} className="zoomed-img" alt="Zoomed view" />
        </div>
      </div>
    )}

      <ContactForm />
    </div>
  );
};

export default ProductDetails;
